import {
  MetricField,
  MetricUnit,
  Reports_GetReportTemplateQuery,
  ReportScheduleRecurrence,
} from '@/__generated__/graphql';
import { useAuth, useModalStatusMessage } from '@/hooks';
import { ApolloError, skipToken, useMutation, useSuspenseQuery } from '@apollo/client';
import { getDefaultUnit } from './getDefaultUnit';
import { type I18n } from '@lingui/core';
import CREATE_REPORT_TEMPLATE from '@features/reports2/graphql/mutation/createReportTemplate';
import EXECUTE_REPORT from '@features/reports2/graphql/mutation/executeReport';
import UPDATE_REPORT_TEMPLATE from '@features/reports2/graphql/mutation/updateReportTemplate';
import { GET_REPORT_TEMPLATE } from '@features/reports2/graphql/queries/getReportTemplate';
import { parseTimeRange } from '@features/reports2/utils/parseTimeRange';
import { getBrowserTimezone } from '@features/reports2/utils/timezone';
import { zodResolver } from '@hookform/resolvers/zod';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { DefaultValues, useForm } from 'react-hook-form';
import { CreateReportFieldValues, CreateReportSchema } from './validation';
import { GET_TEMPLATES } from '@features/reports2/graphql/queries/getReportTemplates';
import { getHoursAndMinutes } from '@features/reports2/utils/schedule';
import { CreateReportsDefaultData } from '@features/reports2/types';

const now = dayjs();

const getDefaultValues = (
  userEmail: string | null | undefined,
  i18n: I18n,
  data?: Reports_GetReportTemplateQuery,
  defaultData?: CreateReportsDefaultData,
) => {
  const values: DefaultValues<CreateReportFieldValues> = {
    step1: {
      devices: [],
      metrics: [],
    },
    step2: {
      period: {
        start: '-7d',
        stop: null,
      },
      meanWindow: '1h',
      timezone: getBrowserTimezone(),
    },
    step3: {
      createExcel: false,
      createPdf: false,
      emails: userEmail ? [{ email: userEmail }] : [],
    },
    step4: {
      enabled: false,
      schedule: {
        recurrence: ReportScheduleRecurrence.Daily,
        time: now,
      },
    },
  };

  if (defaultData) {
    values.step1 = {
      devices: defaultData.devices,
      metrics: defaultData.metrics
        .map((m) => ({ name: m.name, unit: m.unit ?? getDefaultUnit(m.name, i18n) }))
        .filter((m): m is { name: MetricField; unit: MetricUnit } => m.unit != null),
    };
    values.step2 = {
      ...values.step2,
      meanWindow: defaultData.meanWindow,
      period: defaultData.period,
    };
  } else if (data) {
    const template = data.reportTemplate;
    values.step1 = {
      devices: template.devices.map((device) => device.id),
      metrics: template.metrics.map((metric) => ({ name: metric.name, unit: metric.unit })),
    };
    // Sembrado condicional del picker. Si la fila tiene ventana relativa se siembra; si tiene rango
    // absoluto arranca vacío, en vez de con el '-1d' que el backend inventaba. Sembrar ese valor
    // fantasma era lo que convertía en diario un reporte anual al que solo se le editaba un email
    // (AMARU-771). El rango real se muestra en la tabla, no acá: un template no se puede guardar con
    // rango absoluto, así que el picker no tiene cómo representarlo.
    values.step2 = {
      meanWindow: template.meanWindow,
      period: { start: template.relativeTime ?? '', stop: null },
      timezone: template.timezone,
    };
    values.step3 = {
      createExcel: template.createExcel,
      createPdf: template.createPdf,
      emails: template.emails.map((email) => ({ email })),
    };
    const schedule = template.schedules.at(0);
    const step4Base = {
      templateName: template.name,
      templateDescription: template.description ?? undefined,
    };
    if (schedule) {
      const { hours, minutes } = schedule;
      const time = now.set('hours', hours).set('minutes', minutes);
      values.step4 = {
        ...step4Base,
        enabled: true,
        schedule: {
          recurrence: schedule.recurrence,
          dayOfWeek: schedule.dayOfWeek ?? null,
          daysOfWeek: schedule.daysOfWeek ?? null,
          time,
        },
      };
    } else {
      values.step4 = { ...step4Base, enabled: false };
    }
  }

  return values;
};

export const useCreateForm = ({
  templateId,
  defaultData,
  onClose,
}: {
  templateId?: number;
  defaultData?: CreateReportsDefaultData;
  onClose: (skipCloseConfirmation?: boolean) => void;
}) => {
  const { _, i18n } = useLingui();
  const { user } = useAuth();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();

  const { data } = useSuspenseQuery(
    GET_REPORT_TEMPLATE,
    templateId && !defaultData
      ? {
          variables: {
            id: templateId,
          },
        }
      : skipToken,
  );

  const defaultValues = useMemo(() => {
    return getDefaultValues(user?.email, i18n, data, defaultData);
  }, [data, user, defaultData, i18n]);

  const [createTemplate] = useMutation(CREATE_REPORT_TEMPLATE);
  const [executeReport] = useMutation(EXECUTE_REPORT);
  const [updateReportTemplate] = useMutation(UPDATE_REPORT_TEMPLATE);

  const methods = useForm<CreateReportFieldValues>({
    resolver: zodResolver(CreateReportSchema),
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const onSubmit = methods.handleSubmit(async (data) => {
    try {
      const period = parseTimeRange(data.step2.period.start, data.step2.period.stop);

      const parameters = {
        onCompleted() {
          onClose(true);
          openModalSuccessMessage(
            _(msg`Se ha encolado la generación del reporte, lo recibirás en unos momentos`),
          );
        },
        onError(error: ApolloError) {
          openModalErrorMessage(
            _(msg`Ha ocurrido un error al ejecutar el reporte`) + ' ' + error.message,
          );
        },
      };

      // Update template
      if (templateId) {
        const relativeTime = period.relativeTime;
        const { templateName, templateDescription } = data.step4;
        const schedule = data.step4.enabled && data.step4.schedule ? data.step4.schedule : null;

        if (!templateName) {
          openModalErrorMessage(
            _(msg`Faltan datos para guardar el reporte: el nombre es obligatorio`),
          );
          return;
        }

        // La ventana relativa se exige solo al pedir recurrencia (AMARU-771 / D-006): un rango
        // absoluto solo es inválido en combinación con un schedule. Una fila dormida con rango fijo
        // es un reporte puntual legítimo, así que editarle un email omite `relativeTime` del input —
        // Kysely lo deja fuera del SET y la fila conserva su rango. Exigirla siempre sería el bug
        // original invertido: cambiarle el comportamiento a un reporte sin que nadie toque el período.
        if (schedule && !relativeTime) {
          openModalErrorMessage(
            _(
              msg`Para programar este reporte tienes que elegir un período relativo (por ejemplo, el último día o los últimos 7 días)`,
            ),
          );
          return;
        }

        await updateReportTemplate({
          variables: {
            input: {
              id: templateId,
              createPdf: data.step3.createPdf,
              createExcel: data.step3.createExcel,
              devices: data.step1.devices,
              emails: data.step3.emails.map(({ email }) => email),
              metrics: data.step1.metrics,
              timezone: data.step2.timezone,
              meanWindow: data.step2.meanWindow,
              name: templateName,
              description: templateDescription || null,
              ...(relativeTime ? { relativeTime } : {}),
              schedule: schedule
                ? {
                    recurrence: schedule.recurrence,
                    timezone: data.step2.timezone,
                    daysOfWeek: schedule.daysOfWeek ?? null,
                    dayOfWeek: schedule.dayOfWeek ?? null,
                    ...getHoursAndMinutes(schedule.time),
                  }
                : null,
            },
          },
          onCompleted() {
            onClose(true);
            openModalSuccessMessage(_(msg`Se ha guardado el reporte correctamente`));
          },
          onError(error: ApolloError) {
            openModalErrorMessage(
              _(msg`Ha ocurrido un error al guardar el reporte`) + ' ' + error.message,
            );
          },
          refetchQueries: [GET_TEMPLATES],
        });
        return;
      }

      // Creation mode — branch by `enabled` (== schedule wanted). After B5 refine,
      // `enabled=true` guarantees `schedule` is defined; `enabled=false` means one-shot.
      if (!data.step4.enabled) {
        await executeReport({
          variables: {
            input: {
              createPdf: data.step3.createPdf,
              createExcel: data.step3.createExcel,
              devices: data.step1.devices,
              emails: data.step3.emails.map(({ email }) => email),
              metrics: data.step1.metrics,
              timezone: data.step2.timezone,
              meanWindow: data.step2.meanWindow,
              ...period,
            },
          },
          ...parameters,
        });
        return;
      }

      const relativeTime = period.relativeTime;
      const { templateName, templateDescription, schedule } = data.step4;
      if (!relativeTime || !templateName || !schedule) {
        openModalErrorMessage(
          _(
            msg`Faltan datos para programar el reporte: nombre, período y horario son obligatorios`,
          ),
        );
        return;
      }

      await createTemplate({
        variables: {
          input: {
            execute: true,
            createPdf: data.step3.createPdf,
            createExcel: data.step3.createExcel,
            devices: data.step1.devices,
            emails: data.step3.emails.map(({ email }) => email),
            metrics: data.step1.metrics,
            timezone: data.step2.timezone,
            meanWindow: data.step2.meanWindow,
            name: templateName,
            description: templateDescription || null,
            relativeTime,
            schedule: {
              recurrence: schedule.recurrence,
              timezone: data.step2.timezone,
              daysOfWeek: schedule.daysOfWeek ?? null,
              dayOfWeek: schedule.dayOfWeek ?? null,
              ...getHoursAndMinutes(schedule.time),
            },
          },
        },
        refetchQueries: [GET_TEMPLATES],
        ...parameters,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      openModalErrorMessage(_(msg`Ha ocurrido un error al procesar el reporte`) + ' ' + message);
    }
  });

  return { methods, onSubmit };
};
